import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  adminListPartnerships,
  adminCreatePartnership,
  adminUpdatePartnershipStatus,
  adminListLiveCreators,
} from "@/lib/partnerships.functions";

export const Route = createFileRoute("/_authenticated/admin/partnerships")({
  head: () => ({ meta: [{ title: "Partnerships — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: AdminPartnerships,
});

function AdminPartnerships() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListPartnerships);
  const creatorsFn = useServerFn(adminListLiveCreators);
  const createFn = useServerFn(adminCreatePartnership);
  const updateFn = useServerFn(adminUpdatePartnershipStatus);

  const partnerships = useQuery({ queryKey: ["admin-partnerships"], queryFn: () => listFn() });
  const creators = useQuery({ queryKey: ["admin-live-creators"], queryFn: () => creatorsFn() });

  const create = useMutation({
    mutationFn: (data: {
      creator_id: string;
      brand_name: string;
      title: string;
      message: string;
      fixed_fee_gbp: number | null;
      commission_rate: number | null;
    }) => createFn({ data }),
    onSuccess: () => {
      toast.success("Offer sent");
      qc.invalidateQueries({ queryKey: ["admin-partnerships"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to send offer"),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; status: "completed" }) =>
      updateFn({ data: vars }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-partnerships"] });
    },
  });

  const [form, setForm] = useState({
    creator_id: "",
    brand_name: "",
    title: "",
    message: "",
    fixed_fee_gbp: "",
    commission_rate: "",
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.creator_id || !form.brand_name.trim() || !form.title.trim() || !form.message.trim()) {
      toast.error("Fill in creator, brand, title and message");
      return;
    }
    const fixed = form.fixed_fee_gbp.trim() === "" ? null : Number(form.fixed_fee_gbp);
    const ratePct = form.commission_rate.trim() === "" ? null : Number(form.commission_rate);
    create.mutate({
      creator_id: form.creator_id,
      brand_name: form.brand_name.trim(),
      title: form.title.trim(),
      message: form.message.trim(),
      fixed_fee_gbp: fixed,
      commission_rate: ratePct == null ? null : ratePct / 100,
    });
    setForm({ creator_id: "", brand_name: "", title: "", message: "", fixed_fee_gbp: "", commission_rate: "" });
  }

  return (
    <div className="space-y-10">
      <section className="border border-border bg-card p-5">
        <h2 className="font-serif text-xl">Send a partnership offer</h2>
        <form onSubmit={onSubmit} className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-widest text-muted-foreground">Creator</span>
            <select
              value={form.creator_id}
              onChange={(e) => setForm({ ...form, creator_id: e.target.value })}
              className="border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Select creator…</option>
              {(creators.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name} (@{c.handle})</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-widest text-muted-foreground">Brand name</span>
            <input
              value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              maxLength={120}
              className="border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs sm:col-span-2">
            <span className="uppercase tracking-widest text-muted-foreground">Title</span>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={200}
              className="border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs sm:col-span-2">
            <span className="uppercase tracking-widest text-muted-foreground">Message</span>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              maxLength={4000}
              className="border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-widest text-muted-foreground">Fixed fee (£)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.fixed_fee_gbp}
              onChange={(e) => setForm({ ...form, fixed_fee_gbp: e.target.value })}
              className="border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-widest text-muted-foreground">Commission rate (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.commission_rate}
              onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
              className="border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={create.isPending}
              className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-40"
            >
              {create.isPending ? "Sending…" : "Send offer"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-serif text-xl">All partnerships</h2>
        {partnerships.isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : (partnerships.data?.length ?? 0) === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No partnerships yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Creator</th>
                  <th className="px-3 py-2">Brand</th>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {partnerships.data?.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2">{p.creator?.name ?? "—"}</td>
                    <td className="px-3 py-2">{p.brand_name}</td>
                    <td className="px-3 py-2">{p.title}</td>
                    <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {p.status === "accepted" && (
                        <button
                          type="button"
                          onClick={() => update.mutate({ id: p.id, status: "completed" })}
                          className="text-xs uppercase tracking-widest underline hover:opacity-70"
                        >
                          Mark completed
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone: Record<string, string> = {
    pending: "bg-amber-100 text-amber-900",
    accepted: "bg-emerald-100 text-emerald-900",
    declined: "bg-rose-100 text-rose-900",
    completed: "bg-slate-200 text-slate-900",
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${tone[status] ?? "bg-muted"}`}>
      {status}
    </span>
  );
}